import { liveMediaNeedsProductionMode } from "../../../utils/productionMode";
import { isStaffUser, useIsStaffUser } from "../../../utils/staffUser";
import type { WhepLiveStreamStatus } from "./useWhepLiveStream";

/**
 * Why a live view failed (redesign, ENG-2136 follow-up). The player's status
 * union stays coarse and backward compatible ("unavailable", "camera_offline",
 * ...); this rides alongside it so the UI can say WHY in plain words, and
 * staff get a technical detail line support can act on from a screenshot.
 *
 * Kept out of useWhepLiveStream.ts on purpose: several tests mock that module
 * wholesale, and the copy must survive the mock.
 */
export type LiveViewFailureReason =
  // Non-prod build (dev/redesign) talking to staging, which has no streaming
  // fleet and no real cameras. Decided up front; the mint is never called.
  | "needs_production_mode"
  // No camera id to stream.
  | "no_camera"
  // Mint answered 2xx but without the URL/token this protocol needs.
  | "bad_session"
  // Mint POST got no HTTP response (network down, CORS, DNS).
  | "mint_network"
  // Mint POST answered 5xx.
  | "mint_server_error"
  // Mint POST answered some other 4xx (not 401/403-permission/429-quota).
  | "mint_refused"
  // Mint was fine but the camera's stream never came up (cold-start retries
  // ran out on 404s, the start watchdog fired) or it died mid-play.
  | "camera_unreachable"
  // HLS manifest/segment fetches failed at the network layer (status 0 =
  // CORS/blocked/offline, or 401/403 from the CDN).
  | "stream_network"
  // The browser can decode neither via MSE nor natively.
  | "unsupported_browser";

export interface LiveViewFailure {
  reason: LiveViewFailureReason;
  /** Technical cause for the staff detail line, e.g. "HLS fragLoadError · HTTP 403". */
  detail?: string;
}

export const LIVE_VIEW_UNAVAILABLE_MESSAGE =
  "Live View not Available for this site";

export const CAMERA_OFFLINE_MESSAGE =
  "The camera didn't start streaming. It may be offline or unreachable from our servers.";

export const LIVE_STREAM_QUOTA_REACHED_MESSAGE =
  "Monthly live stream quota has been reached for this camera.";

export const LIVE_STREAM_NOT_PERMITTED_MESSAGE =
  "Live streaming is not enabled for your account. Contact your administrator to enable it.";

export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Reload the page to continue watching live video.";

export const CODEC_UNSUPPORTED_MESSAGE =
  "This camera's video format isn't supported on this device. Try Chrome or Safari on newer hardware.";

export const NEEDS_PRODUCTION_MODE_STAFF_MESSAGE =
  "Live view uses real cameras. Switch on Use production (bottom of the page) to watch this camera.";

export const NEEDS_PRODUCTION_MODE_MESSAGE =
  "Live view isn't available on this test site.";

export const STREAM_NETWORK_MESSAGE =
  "Couldn't load the video stream (network). Try reloading; if it keeps happening, report it.";

export const MINT_NETWORK_MESSAGE =
  "Couldn't reach the live view service. Check your connection and try again.";

export const MINT_SERVER_ERROR_MESSAGE =
  "Couldn't start live view (server error).";

export const UNSUPPORTED_BROWSER_MESSAGE =
  "This browser can't play live video. Try Chrome or Safari.";

export const TRY_AGAIN_LABEL = "Try again";

/** Every status that renders a failure overlay instead of video/spinner. */
export const LIVE_VIEW_FAILURE_STATUSES: readonly WhepLiveStreamStatus[] = [
  "unavailable",
  "camera_offline",
  "quota_reached",
  "not_permitted",
  "session_expired",
  "codec_unsupported",
];

export const isLiveViewFailureStatus = (status: WhepLiveStreamStatus) =>
  LIVE_VIEW_FAILURE_STATUSES.includes(status);

/**
 * Live view can't work here at all: a non-prod build (dev/redesign) on the
 * staging API, which has no streaming fleet, with Use production off. Never
 * true on a prod-config build. Shared with talk-down.
 */
export const liveViewNeedsProductionMode = liveMediaNeedsProductionMode;

/** Staff see the technical detail line (internal_user, else super_user). */
export const isLiveViewStaff = isStaffUser;
export const useIsLiveViewStaff = useIsStaffUser;

export interface LiveViewFailureCopy {
  message: string;
  /** Technical detail for staff, or null. */
  detail: string | null;
  /** Offer "Try again" (re-runs play()). */
  retryable: boolean;
}

/**
 * Plain-words message + staff detail + whether a retry can help, for any
 * failure status. `failure` may be null (older callers, mocked hooks): the
 * status alone then decides, as before.
 */
export const describeLiveViewFailure = (
  status: WhepLiveStreamStatus,
  failure: LiveViewFailure | null | undefined,
  { staff = false }: { staff?: boolean } = {}
): LiveViewFailureCopy => {
  const detail = staff ? failure?.detail ?? null : null;
  const copy = (message: string, retryable: boolean): LiveViewFailureCopy => ({
    message,
    detail,
    retryable,
  });

  if (failure?.reason === "needs_production_mode") {
    return copy(
      staff
        ? NEEDS_PRODUCTION_MODE_STAFF_MESSAGE
        : NEEDS_PRODUCTION_MODE_MESSAGE,
      false
    );
  }
  if (status === "quota_reached")
    return copy(LIVE_STREAM_QUOTA_REACHED_MESSAGE, false);
  if (status === "not_permitted")
    // Account-level denial (403 from the mint) — incl. deliberate suspension
    // (ENG-1548). Retrying can't help; say it's the account, not the site.
    return copy(LIVE_STREAM_NOT_PERMITTED_MESSAGE, false);
  if (status === "codec_unsupported")
    // Device can't decode the camera's format (e.g. H.265 without hardware
    // support — Firefox, older machines). Distinct from a dead camera.
    return copy(CODEC_UNSUPPORTED_MESSAGE, false);
  if (status === "session_expired")
    // The mint was rejected for a stale token and a refresh couldn't recover
    // it (ENG-1284) — an auth problem, not a site one. Reload, not retry.
    return copy(SESSION_EXPIRED_MESSAGE, false);
  if (status === "camera_offline") {
    if (failure?.reason === "stream_network")
      return copy(STREAM_NETWORK_MESSAGE, true);
    // The session minted fine but this camera's stream never came up (or
    // died): a per-camera problem — the site-level message would mislead.
    return copy(CAMERA_OFFLINE_MESSAGE, true);
  }
  switch (failure?.reason) {
    case "mint_network":
      return copy(MINT_NETWORK_MESSAGE, true);
    case "mint_server_error":
    case "bad_session":
      return copy(MINT_SERVER_ERROR_MESSAGE, true);
    case "unsupported_browser":
      return copy(UNSUPPORTED_BROWSER_MESSAGE, false);
    case "stream_network":
      return copy(STREAM_NETWORK_MESSAGE, true);
    case "camera_unreachable":
      return copy(CAMERA_OFFLINE_MESSAGE, true);
    default:
      // no_camera, mint_refused (a 4xx such as streaming not set up for the
      // site), or no reason at all.
      return copy(LIVE_VIEW_UNAVAILABLE_MESSAGE, false);
  }
};

/** Short-form copy for compact wall tiles (a ~9px line in a small tile). */
export const describeLiveViewFailureCompact = (
  status: WhepLiveStreamStatus,
  failure: LiveViewFailure | null | undefined,
  options: { staff?: boolean } = {}
): LiveViewFailureCopy => {
  const full = describeLiveViewFailure(status, failure, options);
  const short = (message: string) => ({ ...full, message });
  if (failure?.reason === "needs_production_mode")
    return short(
      options.staff ? "Switch on Use production" : "Not on this test site"
    );
  if (full.message === CAMERA_OFFLINE_MESSAGE)
    return short("Camera didn't start streaming");
  if (full.message === STREAM_NETWORK_MESSAGE)
    return short("Couldn't load video (network)");
  if (full.message === MINT_NETWORK_MESSAGE)
    return short("Couldn't reach live view");
  if (full.message === MINT_SERVER_ERROR_MESSAGE)
    return short("Live view server error");
  if (full.message === CODEC_UNSUPPORTED_MESSAGE)
    return short("Video format not supported here");
  return full;
};

/* ------------------------------------------------------------------ */
/* Detail-line builders used by the player hook.                      */
/* ------------------------------------------------------------------ */

interface AxiosLikeError {
  response?: { status?: number };
  code?: string;
  message?: string;
}

/** Reason + detail for a failed stream-session POST (not quota/403/auth). */
export const classifyMintError = (error: unknown): LiveViewFailure => {
  const err = error as AxiosLikeError | undefined;
  const status = err?.response?.status;
  if (!status) {
    const code = err?.code ? ` ${err.code}` : "";
    return {
      reason: "mint_network",
      detail: `stream-session POST · no response${code} (network/CORS)`,
    };
  }
  if (status >= 500) {
    return {
      reason: "mint_server_error",
      detail: `stream-session POST ${status}`,
    };
  }
  return { reason: "mint_refused", detail: `stream-session POST ${status}` };
};

/** What we remember about the last fatal hls.js error, for classification. */
export interface HlsErrorSummary {
  type?: string;
  details?: string;
  code?: number;
}

export const summarizeHlsError = (data: unknown): HlsErrorSummary => {
  const d = data as
    | {
        type?: string;
        details?: string;
        response?: { code?: number };
        networkDetails?: { status?: number };
      }
    | undefined;
  const code =
    typeof d?.response?.code === "number"
      ? d.response.code
      : typeof d?.networkDetails?.status === "number"
      ? d.networkDetails.status
      : undefined;
  return { type: d?.type, details: d?.details, code };
};

export const formatHlsError = (e: HlsErrorSummary | null | undefined) => {
  if (!e) return "HLS";
  const parts = [`HLS ${e.details ?? e.type ?? "error"}`];
  if (e.code !== undefined) parts.push(`HTTP ${e.code}`);
  return parts.join(" · ");
};

/**
 * A network-layer HLS failure, as opposed to the camera not publishing:
 * status 0 (CORS, blocked, offline) or 401/403 from the CDN/relay. A 404 is
 * the normal cold-start answer (publisher not up yet) and is NOT this.
 */
export const isHardHlsNetworkError = (e: HlsErrorSummary): boolean =>
  e.type === "networkError" &&
  (e.code === 0 || e.code === 401 || e.code === 403);

/**
 * The relay/CDN refused our credentials (401/403). The token is freshly
 * minted at start, so repeating the same request can't help: fail fast.
 * Status 0 is deliberately not here — it is also what a CORS-less error page
 * looks like, so it rides out the cold-start window and is classified at the
 * end instead.
 */
export const isAuthHlsNetworkError = (e: HlsErrorSummary): boolean =>
  e.type === "networkError" && (e.code === 401 || e.code === 403);

export const HEVC_CODEC = /^(hvc1|hev1)/i;
